import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom'
import { StateBlock, ToastProvider } from '@w7/ui'
import { queryRetry, queryRetryDelay } from '@/api/queryRetry'
import { AppShell } from './AppShell'
import { GrantGate } from './GrantGate'
import { DateRangeProvider } from './range'
import { ThemeProvider } from './theme'
import { AuthGate } from '@/auth/AuthGate'
import { CallbackPage } from '@/features/login/CallbackPage'
import { LoginPage } from '@/features/login/LoginPage'
import { NoAccessPage } from '@/features/login/NoAccessPage'
import { HomePage, LegacyFetchRedirect } from '@/features/home/HomePage'
import { ManualCatalogPage } from '@/features/catalog/ManualCatalogPage'
import { CatalogBrowsePage } from '@/features/catalog/CatalogBrowsePage'
import { AutomationPage } from '@/features/catalog/AutomationPage'
import { CompetitionPage } from '@/features/catalog/CompetitionPage'
import { MarketSelectionPage } from '@/features/catalog/MarketSelectionPage'
import { BetfairMarketFetchPage } from '@/features/catalog/BetfairMarketFetchPage'
import { HierarchyPage } from '@/features/hierarchy/HierarchyPage'
import { DownlineListPage } from '@/features/hierarchy/DownlineListPage'
import { CreateDownlinePage } from '@/features/hierarchy/CreateDownlinePage'
import { EditDownlinePage } from '@/features/hierarchy/EditDownlinePage'
import { StatementPage } from '@/features/hierarchy/StatementPage'
import { AggregateMembersPage } from '@/features/hierarchy/AggregateMembersPage'
import { HierarchySearchPage } from '@/features/hierarchy-search/HierarchySearchPage'
import { MemberPage } from '@/features/member/MemberPage'
import { MaxLimitsPage } from '@/features/bet-settings/MaxLimitsPage'
import { MaxLimitLogsPage } from '@/features/bet-settings/MaxLimitLogsPage'
import { VolumePercentPage } from '@/features/bet-settings/VolumePercentPage'
import { BetfairRatesLogsPage } from '@/features/bet-settings/BetfairRatesLogsPage'
import { ClTransfersPage } from '@/features/logs/ClTransfersPage'
import { InvalidBetsPage } from '@/features/logs/InvalidBetsPage'
import { AllCheatersPage } from '@/features/security/AllCheatersPage'
import { ManualGroupBettingPage } from '@/features/security/ManualGroupBettingPage'
import { BlockedDeviceIdsPage } from '@/features/security/BlockedDeviceIdsPage'
import { BlockedIpPage } from '@/features/security/BlockedIpPage'
import { FprintMultipleDeviceIdsPage } from '@/features/security/FprintMultipleDeviceIdsPage'
import { IpWhitelistPage } from '@/features/security/IpWhitelistPage'
import { LoginFromBlockedIpPage } from '@/features/security/LoginFromBlockedIpPage'
import { ManageIpPage } from '@/features/security/ManageIpPage'
import { MultipleDeviceIdsPage } from '@/features/security/MultipleDeviceIdsPage'
import { OddsViolationWhitelistPage } from '@/features/security/OddsViolationWhitelistPage'
import { WhitelistUsersPage } from '@/features/security/WhitelistUsersPage'
import { ChangePasswordPage } from '@/features/security/ChangePasswordPage'
import { ConsoleMfaPage } from '@/features/security/ConsoleMfaPage'
import { CommissionEligibilityPage } from '@/features/reports/CommissionEligibilityPage'
import { MyPnlPage } from '@/features/reports/MyPnlPage'
import { PlStatementPage } from '@/features/reports/PlStatementPage'
import { TransferStatementPage } from '@/features/reports/TransferStatementPage'
import { LiveCasinoPage } from '@/features/vendor-dashboard/LiveCasinoPage'
import { CardGamePage } from '@/features/vendor-dashboard/CardGamePage'
import { BetfairPage } from '@/features/vendor-dashboard/BetfairPage'
import { InstantGamesPage } from '@/features/vendor-dashboard/InstantGamesPage'
import { NetExposurePage } from '@/features/net-exposure/NetExposurePage'
import { WorkstationPage } from '@/features/net-exposure/WorkstationPage'
import { AdvanceWorkstationPage } from '@/features/net-exposure/AdvanceWorkstationPage'
import {
  BetTickerBetfairPage,
  BetTickerFlagFinalPage,
  BetTickerLivePage,
  BetTickerPreviousPage,
} from '@/features/bet-ticker/BetTickerPage'
import { BetfairBalanceDashboardPage } from '@/features/betfair-balance/BetfairBalanceDashboardPage'
import { UsersPage } from '@/features/users/UsersPage'
import { UserFormPage } from '@/features/users/UserFormPage'
import { ManageAccessRolesPage } from '@/features/users/ManageAccessRolesPage'
import { SurveillancePage } from '@/features/surveillance/SurveillancePage'
import { SurveillanceBetaPage } from '@/features/surveillance/SurveillanceBetaPage'

const client = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      // First-load 5xx / Cloud SQL slot exhaustion often succeeds on retry.
      retry: queryRetry,
      retryDelay: queryRetryDelay,
      refetchOnWindowFocus: false,
    },
  },
})

function ShellLayout() {
  return (
    <AuthGate>
      <DateRangeProvider>
        <AppShell>
          <GrantGate>
            <Outlet />
          </GrantGate>
        </AppShell>
      </DateRangeProvider>
    </AuthGate>
  )
}

export function App() {
  return (
    <QueryClientProvider client={client}>
      <ThemeProvider>
        <ToastProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/callback" element={<CallbackPage />} />
              <Route path="/no-access" element={<NoAccessPage />} />
              <Route element={<ShellLayout />}>
                <Route path="/" element={<Navigate to="/home" replace />} />
                <Route path="/home" element={<HomePage />} />
                <Route path="/hierarchy" element={<HierarchyPage />} />
                <Route path="/hierarchy/list" element={<DownlineListPage />} />
                <Route path="/hierarchy/create" element={<CreateDownlinePage />} />
                <Route path="/hierarchy/edit/:id" element={<EditDownlinePage />} />
                <Route path="/hierarchy/statement/:id" element={<StatementPage />} />
                <Route path="/aggregate-members" element={<AggregateMembersPage />} />
                <Route path="/hierarchy-search" element={<HierarchySearchPage />} />
                <Route path="/member/:id" element={<MemberPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/users/new" element={<UserFormPage />} />
                <Route path="/users/access-roles" element={<ManageAccessRolesPage />} />
                <Route path="/users/:id" element={<UserFormPage />} />
                <Route path="/catalog/manual" element={<ManualCatalogPage />} />
                <Route path="/catalog/browse" element={<CatalogBrowsePage />} />
                <Route path="/catalog/automation" element={<AutomationPage />} />
                <Route path="/catalog/competition" element={<CompetitionPage />} />
                <Route path="/catalog/market-selection" element={<MarketSelectionPage />} />
                <Route
                  path="/catalog/bookmaker-selection"
                  element={<Navigate to="/catalog/market-selection" replace />}
                />
                <Route path="/catalog/betfair-fetch" element={<BetfairMarketFetchPage />} />
                <Route path="/bet-settings/max-limits" element={<MaxLimitsPage />} />
                <Route path="/bet-settings/max-limit-logs" element={<MaxLimitLogsPage />} />
                <Route path="/bet-settings/volume-percent" element={<VolumePercentPage />} />
                <Route
                  path="/bet-settings/betfair-rates-logs"
                  element={<Navigate to="/logs/betfair-rates-logs" replace />}
                />
                <Route
                  path="/bet-settings/invalid-bets"
                  element={<Navigate to="/logs/invalid-bets" replace />}
                />
                <Route path="/logs/cl-transfers" element={<ClTransfersPage />} />
                <Route path="/logs/betfair-rates-logs" element={<BetfairRatesLogsPage />} />
                <Route path="/logs/invalid-bets" element={<InvalidBetsPage />} />
                <Route path="/security/manage-ip" element={<ManageIpPage />} />
                <Route path="/security/ip-whitelist" element={<IpWhitelistPage />} />
                <Route path="/security/blocked-ip" element={<BlockedIpPage />} />
                <Route
                  path="/security/login-from-blocked-ip"
                  element={<LoginFromBlockedIpPage />}
                />
                <Route path="/security/whitelist-users" element={<WhitelistUsersPage />} />
                <Route path="/security/blocked-device-ids" element={<BlockedDeviceIdsPage />} />
                <Route path="/security/all-cheaters" element={<AllCheatersPage />} />
                <Route
                  path="/security/odds-violation-whitelist"
                  element={<OddsViolationWhitelistPage />}
                />
                <Route
                  path="/security/manual-group-betting"
                  element={<ManualGroupBettingPage />}
                />
                <Route path="/security/multiple-device-ids" element={<MultipleDeviceIdsPage />} />
                <Route
                  path="/security/fprint-multiple-device-ids"
                  element={<FprintMultipleDeviceIdsPage />}
                />
                <Route path="/security/console-2fa" element={<ConsoleMfaPage />} />
                <Route path="/change-password" element={<ChangePasswordPage />} />
                <Route
                  path="/security/change-password"
                  element={<Navigate to="/change-password" replace />}
                />
                <Route path="/net-exposure" element={<NetExposurePage />} />
                <Route
                  path="/betfair-net-exposure"
                  element={<NetExposurePage variant="betfair" />}
                />
                <Route
                  path="/net-exposure-new"
                  element={<Navigate to="/betfair-net-exposure" replace />}
                />
                <Route
                  path="/betfair-balance-dashboard"
                  element={<BetfairBalanceDashboardPage />}
                />
                <Route path="/betfair-bet-ticker" element={<BetTickerBetfairPage />} />
                <Route path="/bet-ticker" element={<BetTickerLivePage />} />
                {/* Page component kept; route hidden until product wants Master Bet Ticker back. */}
                <Route path="/master-bet-ticker" element={<Navigate to="/bet-ticker" replace />} />
                <Route path="/bet-ticker-previous" element={<BetTickerPreviousPage />} />
                {/* Merged into Bet Ticker Previous (Lab Betfair 7-day tickerBQ switch). */}
                <Route
                  path="/bet-ticker-previous-new"
                  element={<Navigate to="/bet-ticker-previous" replace />}
                />
                <Route path="/bet-ticker-flag-final" element={<BetTickerFlagFinalPage />} />
                <Route path="/reports/my-pnl" element={<MyPnlPage />} />
                <Route path="/reports/pl-statement" element={<PlStatementPage />} />
                <Route
                  path="/reports/transfer-statement"
                  element={<TransferStatementPage />}
                />
                <Route path="/commission-report" element={<CommissionEligibilityPage />} />
                <Route
                  path="/reports/commission-report"
                  element={<Navigate to="/commission-report" replace />}
                />
                <Route
                  path="/viewTransactionNew"
                  element={<Navigate to="/reports/transfer-statement" replace />}
                />
                <Route
                  path="/viewTransactionNew/"
                  element={<Navigate to="/reports/transfer-statement" replace />}
                />
                <Route
                  path="/viewTransaction"
                  element={<Navigate to="/reports/transfer-statement" replace />}
                />
                <Route path="/PLStatement" element={<Navigate to="/reports/pl-statement" replace />} />
                <Route path="/PLStatement/" element={<Navigate to="/reports/pl-statement" replace />} />
                <Route
                  path="/master-dashboard-new"
                  element={<Navigate to="/reports/my-pnl" replace />}
                />
                <Route
                  path="/master-dashboard-new/"
                  element={<Navigate to="/reports/my-pnl" replace />}
                />
                <Route path="/surveillance" element={<SurveillancePage />} />
                <Route path="/surveillance/beta" element={<SurveillanceBetaPage />} />
                <Route
                  path="/surveillance-betlist"
                  element={<Navigate to="/surveillance" replace />}
                />
                <Route
                  path="/surveillance-beta-betlist"
                  element={<Navigate to="/surveillance" replace />}
                />
                <Route path="/vendor-dashboard/live-casino" element={<LiveCasinoPage />} />
                <Route path="/vendor-dashboard/card-game" element={<CardGamePage />} />
                <Route path="/vendor-dashboard/betfair" element={<BetfairPage />} />
                <Route path="/vendor-dashboard/instant-games" element={<InstantGamesPage />} />
                <Route
                  path="/net-exposure/workstation/:marketId"
                  element={<WorkstationPage />}
                />
                <Route
                  path="/net-exposure/advance-workstation/:id"
                  element={<AdvanceWorkstationPage />}
                />
                <Route path="/events/fetch" element={<LegacyFetchRedirect to="/catalog/manual" />} />
                <Route path="/markets/fetch" element={<LegacyFetchRedirect to="/catalog/manual" />} />
                <Route
                  path="*"
                  element={
                    <StateBlock icon="search" title="That page does not exist">
                      Unknown route.
                    </StateBlock>
                  }
                />
              </Route>
            </Routes>
          </BrowserRouter>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}
